import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils/cn';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps } from '@/types';

interface MatrixRow {
  id: string;
  text: string;
  order?: number;
  anchored?: 'top' | 'bottom';
}

interface MatrixColumn {
  id: string;
  text: string;
  value: string | number;
  order?: number;
}

interface MatrixConfig {
  rows?: MatrixRow[];
  columns?: MatrixColumn[];
  // Settings
  randomizeRows?: boolean;
  randomizeColumns?: boolean;
  requireAllRows?: boolean;
  alternateRowColors?: boolean;
  stickyHeader?: boolean;
  mobileLayout?: 'table' | 'cards' | 'accordion';
  showColumnHeaders?: boolean;
  // Scale specific
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
  scaleMidLabel?: string;
  scaleShowLabels?: boolean;
  scaleShowValues?: boolean;
  // Legacy support
  minLabel?: string;
  maxLabel?: string;
  // Display
  headerStyle?: 'default' | 'minimal' | 'bold';
  cellPadding?: 'compact' | 'normal' | 'spacious';
  showRowNumbers?: boolean;
  zebra?: boolean;
  borderStyle?: 'none' | 'light' | 'full';
  highlightOnHover?: boolean;
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function MatrixScale({
  question,
  value,
  onChange,
  disabled,
  errors,
}: QuestionComponentProps<Record<string, number> | null>) {
  const config = question.config as MatrixConfig | undefined;
  const rawRows = config?.rows || [];
  const rawColumns = config?.columns || [];
  const selectedValues = value || {};

  // Scale labels (support both new and legacy config)
  const minLabel = config?.scaleMinLabel || config?.minLabel;
  const maxLabel = config?.scaleMaxLabel || config?.maxLabel;
  const midLabel = config?.scaleMidLabel;
  const showLabels = config?.scaleShowLabels !== false;
  const showValues = config?.scaleShowValues ?? false;

  // Display options with defaults
  const mobileLayout = config?.mobileLayout || 'cards';
  const headerStyle = config?.headerStyle || 'default';
  const cellPadding = config?.cellPadding || 'normal';
  const showRowNumbers = config?.showRowNumbers ?? false;
  const zebra = config?.zebra !== false;
  const borderStyle = config?.borderStyle || 'light';
  const highlightOnHover = config?.highlightOnHover !== false;
  const stickyHeader = config?.stickyHeader !== false;

  // Accordion state for mobile
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Process rows (anchoring and randomization)
  const rows = useMemo(() => {
    const anchored = rawRows.filter((r) => r.anchored);
    const unanchored = rawRows.filter((r) => !r.anchored);

    const processed = config?.randomizeRows ? shuffleArray(unanchored) : unanchored;

    const anchoredTop = anchored.filter((r) => r.anchored === 'top');
    const anchoredBottom = anchored.filter((r) => r.anchored === 'bottom');

    return [...anchoredTop, ...processed, ...anchoredBottom];
  }, [rawRows, config?.randomizeRows]);

  // Process columns (no randomization for scale - order matters)
  const columns = useMemo(() => rawColumns, [rawColumns]);

  const handleSelect = (rowId: string, columnValue: number) => {
    if (disabled) return;
    onChange({ ...selectedValues, [rowId]: columnValue });
  };

  const toggleAccordion = (rowId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const getColumnLabel = (col: MatrixColumn) => {
    if (!showLabels) return showValues ? col.value : '';

    // Show text if available, otherwise show value
    if (col.text && col.text !== String(col.value)) {
      return col.text;
    }
    return showValues ? col.value : '';
  };

  const getMidIndex = () => Math.floor(columns.length / 2);

  const cellPaddingClass =
    cellPadding === 'compact' ? 'p-1' : cellPadding === 'spacious' ? 'p-4' : 'p-2';

  const headerStyleClass =
    headerStyle === 'bold'
      ? 'font-bold bg-gray-100 dark:bg-gray-800'
      : headerStyle === 'minimal'
      ? 'font-normal text-gray-500'
      : 'font-medium';

  const borderClass =
    borderStyle === 'full'
      ? 'border border-gray-200'
      : borderStyle === 'light'
      ? 'border-b border-gray-200'
      : '';

  // Mobile Cards Layout
  const CardsLayout = () => (
    <div className="space-y-3">
      {rows.map((row, idx) => (
        <div key={row.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <p className="font-medium text-gray-900 dark:text-gray-100 mb-3">
            {showRowNumbers && <span className="text-gray-400 mr-2">{idx + 1}.</span>}
            {row.text}
          </p>
          <div className="flex justify-between gap-1">
            {columns.map((col, colIdx) => {
              const numValue = typeof col.value === 'number' ? col.value : parseInt(String(col.value)) || colIdx + 1;
              const isSelected = selectedValues[row.id] === numValue;

              return (
                <button
                  key={col.id}
                  type="button"
                  onClick={() => handleSelect(row.id, numValue)}
                  disabled={disabled}
                  className={cn(
                    'flex-1 py-2 px-1 rounded text-sm font-medium transition-colors min-w-[32px]',
                    isSelected
                      ? 'bg-primary-600 text-white'
                      : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary-400',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {showValues ? col.value : colIdx + 1}
                </button>
              );
            })}
          </div>
          {(minLabel || maxLabel) && (
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>{minLabel}</span>
              {midLabel && <span>{midLabel}</span>}
              <span>{maxLabel}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // Mobile Accordion Layout
  const AccordionLayout = () => (
    <div className="space-y-2">
      {rows.map((row, idx) => {
        const isExpanded = expandedRows.has(row.id);
        const hasSelection = selectedValues[row.id] !== undefined;
        const selectedCol = columns.find(
          (c) => (typeof c.value === 'number' ? c.value : parseInt(String(c.value))) === selectedValues[row.id]
        );

        return (
          <div key={row.id} className="border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => toggleAccordion(row.id)}
              className={cn(
                'w-full flex items-center justify-between p-3 text-left',
                hasSelection && 'bg-primary-50 dark:bg-primary-900/30'
              )}
            >
              <span className="font-medium">
                {showRowNumbers && <span className="text-gray-400 mr-2">{idx + 1}.</span>}
                {row.text}
              </span>
              <div className="flex items-center gap-2">
                {hasSelection && (
                  <span className="text-xs text-primary-600 bg-primary-100 px-2 py-0.5 rounded">
                    {selectedCol?.text || selectedValues[row.id]}
                  </span>
                )}
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </button>
            {isExpanded && (
              <div className="p-3 pt-0 border-t bg-gray-50 dark:bg-gray-800">
                <div className="flex justify-between gap-1 mt-2">
                  {columns.map((col, colIdx) => {
                    const numValue = typeof col.value === 'number' ? col.value : parseInt(String(col.value)) || colIdx + 1;
                    const isSelected = selectedValues[row.id] === numValue;

                    return (
                      <button
                        key={col.id}
                        type="button"
                        onClick={() => handleSelect(row.id, numValue)}
                        disabled={disabled}
                        className={cn(
                          'flex-1 py-2 px-1 rounded text-sm font-medium transition-colors min-w-[32px]',
                          isSelected
                            ? 'bg-primary-600 text-white'
                            : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary-400',
                          disabled && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        {showValues ? col.value : colIdx + 1}
                      </button>
                    );
                  })}
                </div>
                {(minLabel || maxLabel) && (
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>{minLabel}</span>
                    {midLabel && <span>{midLabel}</span>}
                    <span>{maxLabel}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Mobile Table Layout (scrollable)
  const MobileTableLayout = () => (
    <div className="overflow-x-auto -mx-4 px-4">
      <table className="w-full min-w-[400px] border-collapse text-sm">
        <thead className={cn(stickyHeader && 'sticky top-0 z-10 bg-white dark:bg-gray-900')}>
          <tr>
            <th className={cn('text-left', cellPaddingClass, headerStyleClass, borderClass)}>
              &nbsp;
            </th>
            {columns.map((col, colIdx) => (
              <th key={col.id} className={cn('text-center', cellPaddingClass, headerStyleClass, borderClass)}>
                <div>{getColumnLabel(col) || col.value}</div>
                {colIdx === 0 && minLabel && (
                  <div className="text-xs font-normal text-gray-400">{minLabel}</div>
                )}
                {colIdx === getMidIndex() && midLabel && (
                  <div className="text-xs font-normal text-gray-400">{midLabel}</div>
                )}
                {colIdx === columns.length - 1 && maxLabel && (
                  <div className="text-xs font-normal text-gray-400">{maxLabel}</div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={row.id}
              className={cn(
                zebra && idx % 2 === 1 && 'bg-gray-50 dark:bg-gray-800/50',
                highlightOnHover && 'hover:bg-gray-100 dark:hover:bg-gray-800'
              )}
            >
              <td className={cn('font-medium', cellPaddingClass, borderClass)}>
                {showRowNumbers && <span className="text-gray-400 mr-2">{idx + 1}.</span>}
                {row.text}
              </td>
              {columns.map((col, colIdx) => {
                const numValue = typeof col.value === 'number' ? col.value : parseInt(String(col.value)) || colIdx + 1;
                const isSelected = selectedValues[row.id] === numValue;

                return (
                  <td key={col.id} className={cn('text-center', cellPaddingClass, borderClass)}>
                    <button
                      type="button"
                      onClick={() => handleSelect(row.id, numValue)}
                      disabled={disabled}
                      className={cn(
                        'h-6 w-6 rounded-full border-2 transition-colors',
                        isSelected
                          ? 'border-primary-600 bg-primary-600'
                          : 'border-gray-300 hover:border-primary-400',
                        disabled && 'opacity-50 cursor-not-allowed'
                      )}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Desktop table layout
  const DesktopLayout = () => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead className={cn(stickyHeader && 'sticky top-0 z-10 bg-white dark:bg-gray-900')}>
          <tr>
            <th className={cn('text-left min-w-[200px]', cellPaddingClass, headerStyleClass, borderClass)}>
              &nbsp;
            </th>
            {columns.map((col, colIdx) => (
              <th key={col.id} className={cn('text-center', cellPaddingClass, headerStyleClass, borderClass)}>
                <div>{getColumnLabel(col) || col.value}</div>
                {colIdx === 0 && minLabel && (
                  <div className="text-xs font-normal text-gray-400 mt-1">{minLabel}</div>
                )}
                {colIdx === getMidIndex() && midLabel && (
                  <div className="text-xs font-normal text-gray-400 mt-1">{midLabel}</div>
                )}
                {colIdx === columns.length - 1 && maxLabel && (
                  <div className="text-xs font-normal text-gray-400 mt-1">{maxLabel}</div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={row.id}
              className={cn(
                zebra && idx % 2 === 1 && 'bg-gray-50 dark:bg-gray-800/50',
                highlightOnHover && 'hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
              )}
            >
              <td className={cn('font-medium', cellPaddingClass, borderClass)}>
                {showRowNumbers && <span className="text-gray-400 mr-2">{idx + 1}.</span>}
                {row.text}
              </td>
              {columns.map((col, colIdx) => {
                const numValue = typeof col.value === 'number' ? col.value : parseInt(String(col.value)) || colIdx + 1;
                const isSelected = selectedValues[row.id] === numValue;

                return (
                  <td key={col.id} className={cn('text-center', cellPaddingClass, borderClass)}>
                    <button
                      type="button"
                      onClick={() => handleSelect(row.id, numValue)}
                      disabled={disabled}
                      className={cn(
                        'h-8 w-8 rounded-full border-2 transition-colors',
                        isSelected
                          ? 'border-primary-600 bg-primary-600'
                          : 'border-gray-300 hover:border-primary-400',
                        disabled && 'opacity-50 cursor-not-allowed'
                      )}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Mobile layout selector
  const MobileLayout = () => {
    switch (mobileLayout) {
      case 'accordion':
        return <AccordionLayout />;
      case 'table':
        return <MobileTableLayout />;
      case 'cards':
      default:
        return <CardsLayout />;
    }
  };

  return (
    <QuestionWrapper question={question} errors={errors}>
      <div className="sm:hidden">
        <MobileLayout />
      </div>
      <div className="hidden sm:block">
        <DesktopLayout />
      </div>
    </QuestionWrapper>
  );
}
